import React, { useState } from 'react';

// Empty state component
export const EmptyState = ({
  title = "Nothing to show yet",
  description = "Add data or select an option to get started.",
  icon = "📊",
  actionLabel,
  onAction,
  actionAriaLabel,
  autoFocusAction = false,
  actionProps = {}
}) => (
  <div className="empty-state" role="status" aria-live="polite">
    <div className="empty-state__icon" aria-hidden="true">{icon}</div>
    <div className="empty-state__title">{title}</div>
    <div className="empty-state__message">{description}</div>
    {actionLabel && typeof onAction === 'function' && (
      <div className="empty-state__action">
        <button
          type="button"
          onClick={onAction}
          className="btn btn--primary"
          aria-label={actionAriaLabel || actionLabel}
          autoFocus={autoFocusAction}
          {...actionProps}
        >
          {actionLabel}
        </button>
      </div>
    )}
  </div>
);

// Tooltip component for component descriptions
export const InfoTooltip = ({ children, title, content, position = "top-right" }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const getTooltipPosition = () => {
    switch (position) {
      case "top-left":
        return { top: '-25px', left: '0px' };
      case "top-right":
        return { top: '-25px', right: '0px' };
      case "bottom-left":
        return { bottom: '-25px', left: '0px' };
      case "bottom-right":
        return { bottom: '-25px', right: '0px' };
      default:
        return { top: '-25px', right: '0px' };
    }
  };
  
  const getArrowPosition = () => {
    switch (position) {
      case "top-left":
        return { top: '100%', left: '10px' };
      case "top-right":
        return { top: '100%', right: '10px' };
      case "bottom-left":
        return { bottom: '100%', left: '10px' };
      case "bottom-right":
        return { bottom: '100%', right: '10px' };
      default:
        return { top: '100%', right: '10px' };
    }
  };
  
  return (
    <div className="info-tooltip" style={{ display: 'inline-block', marginLeft: '6px', verticalAlign: 'text-top' }}>
      {children}
      <div 
        className="info-tooltip__trigger"
        style={{ display: 'inline-block' }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        ℹ️
        {showTooltip && (
          <div className={`info-tooltip__content info-tooltip__content--${position}`}>
            <div className="info-tooltip__title">{title}</div>
            <div className="info-tooltip__text">{content}</div>
            <div className={`info-tooltip__arrow info-tooltip__arrow--${position}`}></div>
          </div>
        )}
      </div>
    </div>
  );
};

// Loading state component
export const LoadingState = ({ message = "Loading..." }) => (
  <div className="loading-state">
    <div className="loading-state__icon">⏳</div>
    <div className="loading-state__message">{message}</div>
  </div>
);

// Error state component
export const ErrorState = ({ message, onRetry = null }) => (
  <div className="error-state">
    <div className="error-state__icon">❌</div>
    <div className="error-state__title">Error</div>
    <div className="error-state__message">{message}</div>
    {onRetry && (
      <button 
        onClick={onRetry}
        className="error-state__retry-btn"
      >
        Try Again
      </button>
    )}
  </div>
);

export const InlineNotice = ({ type = 'info', message }) => {
  if (!message) return null;
  const className = type === 'error' ? 'error-message' : type === 'success' ? 'success-message' : 'message';
  return <div className={className}>{message}</div>;
};

export const ConfirmDialog = ({
  open,
  title = 'Please confirm',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  loading = false,
}) => {
  if (!open) return null;
  return (
    <div className="feedback-modal-overlay" role="dialog" aria-modal="true">
      <div className="feedback-modal">
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="feedback-modal-actions">
          <button className="btn btn--outline" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </button>
          <button className="btn btn--danger" onClick={onConfirm} disabled={loading}>
            {loading ? 'Working...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// Table component for displaying data (instead of raw JSON)
export const DataTable = ({ columns, data }) => (
  <div className="table-container">
    <table className="data-table">
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col.accessor}>{col.header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {columns.map((col) => (
              <td key={col.accessor}>{row[col.accessor]}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);


// A simple 2-column table for summarizing key-value data
export const SummaryTable = ({ title, data }) => {
  if (!data) return null;

  return (
    <div className="summary-table-wrapper mb-6">
      <h4 className="font-bold text-md mb-2 text-gray-700">{title}</h4>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data).map(([key, value]) => (
              <tr key={key}>
                <td className="font-medium text-gray-600">{key}</td>
                <td>
                  {typeof value === 'number' 
                    ? value.toLocaleString(undefined, { maximumFractionDigits: 4 }) 
                    : value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


export const CorrelationMatrix = ({ data }) => {
  if (!data) return null;

  // The keys are both the rows and the columns
  const labels = Object.keys(data);

  return (
    <div className="correlation-matrix-wrapper mt-6">
      <h4 className="font-bold text-md mb-2 text-gray-700">Correlation Matrix</h4>
      <div className="table-container" style={{ maxHeight: '600px' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th className="bg-gray-100 z-20 sticky top-0 left-0"></th> {/* Empty corner */}
              {labels.map(label => (
                <th key={label} className="text-xs rotate-45 h-32 w-8 align-bottom">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {labels.map(rowLabel => (
              <tr key={rowLabel}>
                {/* Sticky Row Header */}
                <td className="font-bold text-xs bg-gray-50 sticky left-0 z-10 border-r">
                  {rowLabel}
                </td>
                {/* Data Cells */}
                {labels.map(colLabel => {
                  const val = data[rowLabel][colLabel];
                  return (
                    <td 
                      key={colLabel} 
                      className="text-center text-xs"
                      title={`${rowLabel} vs ${colLabel}: ${val}`}
                    >
                      {val.toFixed(2)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};